import { useCallback } from 'react';

import type {
	AnalyticsEventAttributes,
	EventKey,
	LuminaInteractedAttributesType,
} from './generated/analytics.types';
import { useAnalyticsEvents } from './generated/use-analytics-events';
import { useLuminaSharedAttributes } from './lumina-analytics-context';

// ---------------------------------------------------------------------------
// Discriminated union for ui.lumina.interacted
//
// Callers pass only the fields relevant to the specific interaction.
// The hook fills omitted optional fields with null before forwarding to the
// generated fireEvent, which expects the full flat LuminaInteractedAttributesType.
// ---------------------------------------------------------------------------

type SmartLinkClickedAttrs = {
	cardVariant: string | null;
	componentType: 'smartLink';
	containerType?: 'carousel' | null;
	indexInContainer?: number | null;
	interactionType: 'clicked';
	product: string | null;
	sectionKey?: string | null;
};

type SectionToggledAttrs = {
	componentType: 'section';
	interactionType: 'toggled';
	newState: 'expanded' | 'collapsed';
	sectionKey?: string | null;
};

/** Union of all narrowed interacted attribute shapes. */
type NarrowedInteractedAttrs = SmartLinkClickedAttrs | SectionToggledAttrs;

/**
 * Narrowed attribute map that overrides the interacted event with the
 * discriminated union while keeping all other events (like errored) as-is.
 * New events from codegen are automatically included without manual updates.
 */
type NarrowedEventAttributes = Omit<AnalyticsEventAttributes, 'ui.lumina.interacted'> & {
	'ui.lumina.interacted': NarrowedInteractedAttrs;
};

// Per-event defaults — used to fill omitted optional fields with null.
// Events not listed here have no defaults to merge.
const EVENT_DEFAULTS: {
	[K in EventKey]?: K extends 'ui.lumina.interacted' ? LuminaInteractedAttributesType : never;
} = {
	'ui.lumina.interacted': {
		componentType: 'smartLink',
		interactionType: 'clicked',
		sectionKey: null,
		product: null,
		cardVariant: null,
		containerType: null,
		indexInContainer: null,
		newState: null,
	},
};

/**
 * Lumina analytics hook with discriminated-union narrowing.
 *
 * Wraps the generated `useAnalyticsEvents` hook so that callers of
 * `fireEvent('ui.lumina.interacted', ...)` only need to supply the fields
 * relevant to the specific `componentType`/`interactionType` combination.
 * Omitted optional fields are automatically filled with `null`.
 *
 * Shared context fields (`traceId`, `spanId`, `packageName`, `packageVersion`)
 * are injected directly into every event's `attributes` object via
 * `LuminaAnalyticsContext`. This is necessary because `FabricAnalyticsListeners`
 * only extracts standard fields (like `source`) from `AnalyticsContext` —
 * custom fields placed there are dropped from the final GAS event.
 *
 * `source` is NOT injected here — it is handled by `AnalyticsContext` and
 * correctly extracted to the top-level `source` field by the listener.
 *
 * @example
 * ```tsx
 * const { fireEvent } = useLuminaAnalyticsEvents();
 *
 * // Smart link click — only smart-link-relevant fields
 * fireEvent('ui.lumina.interacted', {
 *   componentType: 'smartLink',
 *   interactionType: 'clicked',
 *   product: 'Confluence',
 *   cardVariant: 'COMPACT',
 * });
 *
 * // Section toggle — only section-relevant fields
 * fireEvent('ui.lumina.interacted', {
 *   componentType: 'section',
 *   interactionType: 'toggled',
 *   sectionKey: '1',
 *   newState: 'expanded',
 * });
 *
 * // Error — unchanged
 * fireEvent('operational.lumina.errored', {
 *   errorType: 'tagParse',
 *   context: null,
 * });
 * ```
 */
export const useLuminaAnalyticsEvents = () => {
	const { fireEvent: rawFireEvent } = useAnalyticsEvents();
	const sharedAttributes = useLuminaSharedAttributes();

	const fireEvent = useCallback(
		<K extends EventKey>(
			key: K,
			attrs: NarrowedEventAttributes[K],
		): void => {
			// Merge: per-event defaults (null-fills) → shared attributes → caller attributes.
			// Shared attributes (packageName, packageVersion, traceId, spanId) are spread
			// into every event's attributes. The generated types don't include these fields,
			// so we cast through unknown.
			// Cast is necessary because we're merging shared attributes that aren't in the
			// generated types. Tests in use-lumina-analytics-test.tsx provide runtime safety.
			const mergedAttrs = {
				...EVENT_DEFAULTS[key],
				...sharedAttributes,
				...attrs,
			};

			// Compile-time safety check: verify merged attributes are structurally compatible.
			// Never executes at runtime (zero performance impact) — the `if (false)` is eliminated
			// by minifiers and tree-shaking. TypeScript still type-checks the code inside, catching
			// cases where required fields are missing or types mismatch.
			if (false as boolean) {
				const _check: AnalyticsEventAttributes[K] = mergedAttrs as any;
				void _check;
			}

			rawFireEvent(key, mergedAttrs as unknown as AnalyticsEventAttributes[K]);
		},
		[rawFireEvent, sharedAttributes],
	);

	return { fireEvent };
};
